/**
 * A wrapper around EntityInput for cases where you are just specifying a single entity. In particular,
 * when you don't need state about which entity has been selected, and want an action to be submitted
 * immediately after selection.
 */

import PropTypes from 'prop-types';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import notifications from 'notifications';
import {ButtonProps, ButtonStyle} from 'ui-library/buttons/Button';
import {EntityWithText} from 'ui-library/entity-input/EntityInputConstants';
import {EntityInputWithSearch} from 'ui-library/entity-input/EntityInputWithSearch';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import strUtil from 'util/strUtil';
import {ElementKeyboardEvent, ElementMouseEvent} from 'util/Types';

export interface SingleEntityProps {
  // If allowEmails is true, an email will show up as a clickable suggestion if the user inputs an email.
  // This also allows emails to be passed to the onSubmit handler as {email}.
  allowEmails?: boolean;
  // If allowTeams is true, teams will be included as entity suggestions.
  allowTeams?: boolean;
  // If allowOrganizations is true, organizations will be included as entity suggestions.
  allowOrganizations?: boolean;
  // If allowBenchlingApps is true, benchApps will be included as entity suggestions.
  allowBenchlingApps?: boolean;
  organizationIds?: Array<string>;

  enableNewEntityInput?: boolean;

  // passed down to TagsInput
  addPredicate?: (e: ElementKeyboardEvent) => boolean;
  buttonProps?: ButtonProps;
  emptyText?: string;
  autoFocus?: boolean;

  // A function to filter the entities. Should return a list of entities
  // that should be rendered as options.
  filterOptions?: (text: string, options: Array<EntityWithText>) => Array<EntityWithText>;
  onChangeInput?: (text: string) => void;
  // A handler function called, which is triggered when the user selects an option. The submit handler
  // is called with {entity} if the user selected an existing entity, {email} if the user inputs an email
  // and allowEmails is true, and {handle} otherwise as the catch-all.
  onSubmit: ({
    entity,
    email,
    handle,
  }: {
    entity?: Partial<EntityWithText>;
    email?: string;
    handle?: string;
  }) => void;

  placeholder?: string;
  size: TagsInputSize;
}

interface State {
  hoveredOption: Partial<EntityWithText>;
}

export class SingleEntityInput extends React.Component<SingleEntityProps> {
  static propTypes = {
    allowEmails: PropTypes.bool,
    allowTeams: PropTypes.bool,
    allowOrganizations: PropTypes.bool,
    allowBenchlingApps: PropTypes.bool,
    organizationIds: PropTypes.array,
    enableNewEntityInput: PropTypes.bool,
    addPredicate: PropTypes.func,
    buttonProps: PropTypes.shape({
      buttonStyle: PropTypes.oneOf(Object.values(ButtonStyle)).isRequired,
      size: PropTypes.string.isRequired,
      onClick: PropTypes.func,
    }),
    emptyText: PropTypes.string,
    autoFocus: PropTypes.bool,
    filterOptions: PropTypes.func,
    onChangeInput: PropTypes.func,
    onSubmit: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    size: PropTypes.string,
  };

  override state: State = {
    hoveredOption: {text: ''},
  };

  _handleClickButton = (e: ElementMouseEvent): void => {
    e.preventDefault();
    this._handleSelectOption([this.state.hoveredOption]);

    // This method is installed as the main button handler. But we invoke the user's own
    // click handler, if provided. (We know buttonProps exists, otherwise this function
    // wouldn't be invoked.)
    if (this.props.buttonProps!.onClick) {
      this.props.buttonProps!.onClick(e);
    }
  };

  _handleSelectOption = (options: Array<Partial<EntityWithText>>): void => {
    assertAndContinue(
      options.length <= 1,
      'SingleEntityInput._handleSelectOption must be passed at most one option'
    );
    const option = options[0];
    if (!option) {
      return;
    }
    if (option.handle) {
      this.props.onSubmit({entity: option});
    } else if (
      this.props.allowEmails &&
      ((option.text && strUtil.isValidEmail(option.text)) ||
        (option.email && strUtil.isValidEmail(option.email)))
    ) {
      this.props.onSubmit({email: option.email || option.text});
    } else if (option.text && option.text.trim()) {
      notifications.error(`Sorry, no one named ${option.text} found.`);
    }
  };

  _handleHoverOption = (option: EntityWithText): void => {
    this.setState({hoveredOption: option});
  };

  override render(): JSX.Element {
    const {buttonProps, onSubmit, ...passThroughProps} = this.props;
    return (
      <EntityInputWithSearch
        value={[]}
        // We want to handle showing errors for invalid users in this component, so we allow
        // unvalidated users in EntityInput.
        allowUnvalidatedEntities={true}
        buttonProps={
          buttonProps && {
            ...buttonProps,
            // The user's `onClick` handler is invoked inside ours (above)
            onClick: this._handleClickButton,
          }
        }
        onChange={this._handleSelectOption}
        onHoverOption={this._handleHoverOption}
        {...passThroughProps}
      />
    );
  }
}
